import type {
  LateralizationResult,
  OrientationResult,
  ParsedValues,
  SpectrumComputedRow,
  SpectrumDefinition,
  SpectrumResult,
} from '@/components/sop-intake/sop-analyzer/types';
import type { IntakeLateralizationState } from '@/components/sop-intake/sop-analyzer/lateralization-explainer';
import type { RuleDocVersion } from '@/lib/posture-analysis/rule-params';
import { getEvaluationRules } from '@/lib/posture-analysis/evaluation-rules-load';
import {
  getExpectationForTestKey,
  lateralizationBiasFromLimitations,
} from '@/components/sop-intake/sop-analyzer/engine/lateralization-expectations';

const evaluationRules = () => getEvaluationRules() as any;

/** YAML `sop.thresholds.lateralizationDeltaMinDeg` — balanced highlight / Even bias when |L−R| is below this. */
export const getLateralizationDeltaMinDeg = (): number =>
  evaluationRules()?.sop?.thresholds?.lateralizationDeltaMinDeg ?? 5;

const CERVICAL_BUFFER = 5;
const DEFAULT_ROM_BUFFER = evaluationRules()?.sop?.thresholds?.sopPassFailBufferDeg ?? 10;

const getSlrHypermobileUpperLimitDeg = (): number =>
  evaluationRules()?.sop?.thresholds?.slrHypermobileUpperLimitDeg ?? 110;

const normalizeCell = (cell: string): string => cell.replace(/≥/g, '>').replace(/\*/g, '').trim();

const findCellIndex = (cells: string[], target: string): number =>
  cells.findIndex((cell) => normalizeCell(cell) === target);

const findAllCellIndexes = (cells: string[], target: string): number[] =>
  cells
    .map((cell, index) => ({ cell, index }))
    .filter((entry) => normalizeCell(entry.cell) === target)
    .map((entry) => entry.index);

const getBuffer = (testKey: string): number => {
  if (testKey === 'CervicalAxialRotation' || testKey === 'CervicalLateralFlexion') {
    return CERVICAL_BUFFER;
  }
  return DEFAULT_ROM_BUFFER;
};

const getPassThreshold = (def: SpectrumDefinition): number | null => {
  if (def.nominal === null) return null;
  if (def.passThresholdOverride !== undefined) return def.passThresholdOverride;
  return def.nominal - getBuffer(def.key);
};

const passes = (value: number | null, threshold: number | null): boolean | null => {
  if (value === null || threshold === null) return null;
  return value >= threshold;
};

/**
 * True when both sides are at/above nominal and at least one is strictly above (hypermobile vs goal).
 * Hip Extension (goal 0°, signed ROM) is excluded.
 */
function isHypermobileVersusNominalGoal(def: SpectrumDefinition, l: number, r: number): boolean {
  if (def.key === 'HipExtension') return false;
  const n = def.nominal;
  if (n === null || n <= 0) return false;
  return l >= n && r >= n && (l > n || r > n);
}

/**
 * Hypermobile AIA: invert distance from optimum — surplus s = value − nominal, then
 * limSide = max(sL,sR) − sSide so the side closer to goal has higher "limitation" for comparison.
 */
function computeHypermobileInvertedLimitations(
  def: SpectrumDefinition,
  l: number,
  r: number
): { limL: number; limR: number } | null {
  if (!isHypermobileVersusNominalGoal(def, l, r)) return null;
  const n = def.nominal!;
  const sL = l - n;
  const sR = r - n;
  const m = Math.max(sL, sR);
  return { limL: m - sL, limR: m - sR };
}

/**
 * AIA-style deficit per side: 0 when the side passes the pass line; else shortfall vs nominal goal.
 * Used for simple L/R on Biased + Mixed bilateral rows (not SLR). Obers / null nominal skips.
 */
function computeAiaLimitationDeficits(
  def: SpectrumDefinition,
  l: number,
  r: number
): { limL: number; limR: number } | null {
  if (def.nominal === null) return null;
  const t = getPassThreshold(def);
  if (t === null) return null;
  const n = def.nominal;
  const limL = passes(l, t) ? 0 : Math.max(0, n - l);
  const limR = passes(r, t) ? 0 : Math.max(0, n - r);
  return { limL, limR };
}

/**
 * Excel CF pattern per assessment (docs/Automated-SoP-v4, fixtures under test/unit/sop-evaluation/fixtures).
 * Determines which "No Limit" corner (K vs P) fires when both sides pass the lateralization threshold.
 */
export type ExcelSpectrumPattern = 'A' | 'B' | 'B2' | 'C';

export function getExcelSpectrumPattern(testKey: string): ExcelSpectrumPattern {
  switch (testKey) {
    case 'CervicalAxialRotation':
      return 'B';
    case 'ShoulderER':
    case 'ShoulderFlexion':
    case 'ShoulderAbduction':
    case 'PelvicRotation':
    case 'FemoralIR':
    case 'HipExtension':
      return 'B2';
    case 'ShoulderIR':
    case 'HipAbduction':
      return 'A';
    case 'CervicalLateralFlexion':
    case 'FemoralER':
      return 'C';
    default:
      return 'A';
  }
}

/**
 * When both sides pass the pass line, which "No Limit" spectrum columns highlight (K-only, P-only, or both).
 * Aligns with AIA / Excel: limitation asymmetry within the functional band still picks a dominant column.
 */
export function computeBothPassNoLimitColumnIndices(
  l: number,
  r: number,
  t: number,
  pattern: ExcelSpectrumPattern,
  cells: string[]
): number[] {
  const noLimitIdxs = findAllCellIndexes(cells, 'No Limit');
  const kIdx = noLimitIdxs[0] ?? 0;
  const pIdx = noLimitIdxs[noLimitIdxs.length - 1] ?? 5;
  if (l < t || r < t) return [];

  const deltaMin = getLateralizationDeltaMinDeg();
  if (Math.abs(l - r) < deltaMin) return [kIdx, pIdx];

  if (pattern === 'A' || pattern === 'C') {
    if (l < r) return [kIdx];
    return [pIdx];
  }
  // B and B2: K when I<=H (R<=L); P when H<=I (L<=R)
  if (r < l) return [kIdx];
  return [pIdx];
}

function deriveSlrRowState(active: number[], l: number, r: number, upper: number): SpectrumRowState {
  if (active.length === 0) return 'No Data';
  if (l >= upper || r >= upper) return 'Hypermobile Override';
  if (active.includes(1) || active.includes(4)) return 'Biased Lateralization';
  if (active.includes(2) || active.includes(3)) return 'Mixed Lateralization';
  if (active.includes(0) || active.includes(5)) return 'Functional Occupancy';
  return 'No Data';
}

/**
 * Straight Leg Raise — full Excel CF rules (see sop-spectrum.test.ts slrSpectrum).
 */
function computeSlrSpectrumPartial(
  def: SpectrumDefinition,
  l: number,
  r: number,
  t: number,
  upper: number
): BilateralSpectrumPartial {
  const leftPass = passes(l, t);
  const rightPass = passes(r, t);
  const H = l;
  const I = r;
  const active: number[] = [];

  // K: AND(H>t, I>t, I<upper, I<=H)
  if (H > t && I > t && I < upper && I <= H) active.push(0);
  // L: AND(H>=t, I<t, H<upper)
  if (H >= t && I < t && H < upper) active.push(1);
  // M: (both < t, I<=H) OR (I>=upper, I>=H)
  if (H < t && I < t && I <= H) active.push(2);
  else if (I >= upper && I >= H) active.push(2);
  // N: (both < t, H<=I) OR (H>=upper, H>=I) — separate from M so both can fire when L=R
  if (H < t && I < t && H <= I) active.push(3);
  else if (H >= upper && H >= I) active.push(3);
  // O: AND(H<t, I>=t, I<upper)
  if (H < t && I >= t && I < upper) active.push(4);
  // P: AND(H>=t, I>=t, H<upper, I<upper, H<=I)
  if (H >= t && I >= t && H < upper && I < upper && H <= I) active.push(5);

  const rowState = deriveSlrRowState(active, l, r, upper);

  return {
    rowState,
    activeColumns: active,
    passThreshold: t,
    leftPass,
    rightPass,
  };
}

/** Maps spectrum matrix column indices to SoP Table simple-view L vs R columns (see LateralizationDetailDrawer). */
export function getSpectrumSimpleSideHighlight(activeColumns: number[]): { left: boolean; right: boolean } {
  return {
    left: activeColumns.some((col) => col <= 2),
    right: activeColumns.some((col) => col >= 3),
  };
}

export type BilateralSpectrumPartial = {
  rowState: SpectrumComputedRow['rowState'];
  activeColumns: number[];
  passThreshold: number | null;
  leftPass: boolean | null;
  rightPass: boolean | null;
};

/** Legacy Ober's row — same logic as computeSpectrumRows HipAdductionObers branch. */
export function computeHipAdductionObersSpectrumPartial(
  def: SpectrumDefinition,
  l: number | null,
  r: number | null
): BilateralSpectrumPartial {
  const leftLegacyPass = l !== null ? l > 0 : null;
  const rightLegacyPass = r !== null ? r > 0 : null;

  if (l === null && r === null) {
    return {
      rowState: 'No Data',
      activeColumns: [],
      passThreshold: null,
      leftPass: leftLegacyPass,
      rightPass: rightLegacyPass,
    };
  }

  if (l === null || r === null) {
    return {
      rowState: 'Insufficient Data',
      activeColumns: [],
      passThreshold: null,
      leftPass: leftLegacyPass,
      rightPass: rightLegacyPass,
    };
  }

  if (leftLegacyPass && rightLegacyPass) {
    return {
      rowState: 'Functional Occupancy',
      activeColumns: findAllCellIndexes(def.cells, 'No Limit'),
      passThreshold: null,
      leftPass: leftLegacyPass,
      rightPass: rightLegacyPass,
    };
  }

  if ((leftLegacyPass && !rightLegacyPass) || (!leftLegacyPass && rightLegacyPass)) {
    const failingSide = leftLegacyPass ? 'R' : 'L';
    const intactCell = findCellIndex(def.cells, `Only [${failingSide}]`);
    return {
      rowState: 'Biased Lateralization',
      activeColumns: intactCell >= 0 ? [intactCell] : [],
      passThreshold: null,
      leftPass: leftLegacyPass,
      rightPass: rightLegacyPass,
    };
  }

  const leftMixedIdx = findCellIndex(def.cells, '[L] > [R]');
  const rightMixedIdx = findCellIndex(def.cells, '[R] > [L]');
  const activeColumns =
    l === r
      ? [rightMixedIdx, leftMixedIdx].filter((idx) => idx >= 0)
      : l > r
        ? leftMixedIdx >= 0
          ? [leftMixedIdx]
          : []
        : rightMixedIdx >= 0
          ? [rightMixedIdx]
          : [];

  return {
    rowState: 'Mixed Lateralization',
    activeColumns,
    passThreshold: null,
    leftPass: leftLegacyPass,
    rightPass: rightLegacyPass,
  };
}

/**
 * Standard bilateral ROM spectrum row (pass threshold + mixed asymmetry), shared with intake cards.
 * Not used for FootPresentation, GroundingPerformance, or HipAdductionObers.
 */
export function computeStandardBilateralSpectrumPartial(
  def: SpectrumDefinition,
  l: number | null,
  r: number | null
): BilateralSpectrumPartial {
  const threshold = getPassThreshold(def);
  const leftPass = passes(l, threshold);
  const rightPass = passes(r, threshold);

  if (l === null && r === null) {
    return {
      rowState: 'No Data',
      activeColumns: [],
      passThreshold: threshold,
      leftPass,
      rightPass,
    };
  }

  if (l === null || r === null) {
    return {
      rowState: 'Insufficient Data',
      activeColumns: [],
      passThreshold: threshold,
      leftPass,
      rightPass,
    };
  }

  if (def.key === 'StraightLegRaise' && threshold !== null) {
    return computeSlrSpectrumPartial(def, l, r, threshold, getSlrHypermobileUpperLimitDeg());
  }

  if (leftPass === true && rightPass === true) {
    const t = threshold ?? 0;
    const pattern = getExcelSpectrumPattern(def.key);
    const activeColumns =
      threshold === null
        ? findAllCellIndexes(def.cells, 'No Limit')
        : computeBothPassNoLimitColumnIndices(l, r, t, pattern, def.cells);
    return {
      rowState: 'Functional Occupancy',
      activeColumns,
      passThreshold: threshold,
      leftPass,
      rightPass,
    };
  }

  if ((leftPass === true && rightPass === false) || (leftPass === false && rightPass === true)) {
    const failingSide = leftPass === false ? 'L' : 'R';
    const intactCell = findCellIndex(def.cells, `Only [${failingSide}]`);
    return {
      rowState: 'Biased Lateralization',
      activeColumns: intactCell >= 0 ? [intactCell] : [],
      passThreshold: threshold,
      leftPass,
      rightPass,
    };
  }

  if (leftPass === false && rightPass === false) {
    if (Math.abs(l - r) < getLateralizationDeltaMinDeg()) {
      return {
        rowState: 'Functional Occupancy',
        activeColumns: findAllCellIndexes(def.cells, 'No Limit'),
        passThreshold: threshold,
        leftPass,
        rightPass,
      };
    }

    const leftMixedIdx = findCellIndex(def.cells, '[L] > [R]');
    const rightMixedIdx = findCellIndex(def.cells, '[R] > [L]');
    const activeColumns =
      l === r
        ? [rightMixedIdx, leftMixedIdx].filter((idx) => idx >= 0)
        : l > r
          ? leftMixedIdx >= 0
            ? [leftMixedIdx]
            : []
          : rightMixedIdx >= 0
            ? [rightMixedIdx]
            : [];

    return {
      rowState: 'Mixed Lateralization',
      activeColumns,
      passThreshold: threshold,
      leftPass,
      rightPass,
    };
  }

  return {
    rowState: 'No Data',
    activeColumns: [],
    passThreshold: threshold,
    leftPass,
    rightPass,
  };
}

/**
 * Spectrum L/R highlight for intake measurement cards — matches SoP Table simple columns for bilateral tests.
 * - **Hypermobile** (both sides at/above nominal goal, at least one strictly above): inverted surplus
 *   vs optimum, then {@link lateralizationBiasFromLimitations} (includes SLR / FO when applicable).
 * - **Deficit** (Biased + Mixed, not SLR): shortfall vs goal when side fails pass line.
 * {@link computeLateralization} aggregate bias may still use ROM delta.
 */
export function computeIntakeSpectrumHighlight(
  testKey: string,
  l: number | null,
  r: number | null,
  version: RuleDocVersion
): {
  activeColumns: number[];
  rowState: SpectrumComputedRow['rowState'];
  simple: { left: boolean; right: boolean };
} | null {
  const defs = getSpectrumDefinitions(version);
  const def = defs.find((d) => d.key === testKey);
  if (!def || def.key === 'FootPresentation' || def.key === 'GroundingPerformance') {
    return null;
  }

  const partial =
    def.key === 'HipAdductionObers'
      ? computeHipAdductionObersSpectrumPartial(def, l, r)
      : computeStandardBilateralSpectrumPartial(def, l, r);

  let simple = getSpectrumSimpleSideHighlight(partial.activeColumns);
  const expectation = getExpectationForTestKey(testKey);

  const applyLimitationBias = (lims: { limL: number; limR: number } | null) => {
    if (!lims || !expectation) return;
    const bias = lateralizationBiasFromLimitations(lims.limL, lims.limR, expectation);
    if (bias === 'Even') {
      simple = { left: true, right: true };
    } else if (bias === 'Left') {
      simple = { left: true, right: false };
    } else {
      simple = { left: false, right: true };
    }
  };

  if (l !== null && r !== null) {
    /** Femoral IR: small bilateral delta while both pass → balanced FO corners; surplus inversion must not erase that highlight. */
    const suppressHyperInvertedSurplus =
      def.key === 'FemoralIR' &&
      partial.rowState === 'Functional Occupancy' &&
      partial.leftPass === true &&
      partial.rightPass === true &&
      Math.abs(l - r) < getLateralizationDeltaMinDeg();

    const hyperLims = suppressHyperInvertedSurplus ? null : computeHypermobileInvertedLimitations(def, l, r);
    if (hyperLims) {
      applyLimitationBias(hyperLims);
    } else {
      const useDeficitLimitationSimple =
        (partial.rowState === 'Biased Lateralization' || partial.rowState === 'Mixed Lateralization') &&
        def.key !== 'StraightLegRaise';
      if (useDeficitLimitationSimple) {
        applyLimitationBias(computeAiaLimitationDeficits(def, l, r));
      }
    }
  }

  return {
    activeColumns: partial.activeColumns,
    rowState: partial.rowState,
    simple,
  };
}

/** Maps spectrum result to intake card lateralization (matches SoP Table simple view). */
export function spectrumHighlightToIntakeState(
  spec: NonNullable<ReturnType<typeof computeIntakeSpectrumHighlight>>
): IntakeLateralizationState {
  if (spec.rowState === 'Functional Occupancy') {
    // K-only or P-only FO = directional; both K and P corners (No Limit at 0 and 5) = symmetric / balanced
    const sorted = [...spec.activeColumns].sort((a, b) => a - b);
    if (sorted.length === 2 && sorted[0] === 0 && sorted[1] === 5) return 'balanced';
    const { left: sL, right: sR } = spec.simple;
    if (sL && !sR) return 'left';
    if (!sL && sR) return 'right';
    if (sL && sR) return 'mixed';
    return 'balanced';
  }
  if (spec.rowState === 'No Data' || spec.rowState === 'Insufficient Data') return 'none';
  const { left: sL, right: sR } = spec.simple;
  if (sL && sR) return 'mixed';
  if (sL && !sR) return 'left';
  if (!sL && sR) return 'right';
  return 'none';
}

/**
 * Single source of truth for Assessment Values cards and SoP Table **simple** L/R columns.
 * Uses the same spectrum engine as Detailed table rows (`computeSpectrumRows` → bilateral / Obers partials).
 * Simple state follows AIA deficit limitations (Biased/Mixed) and inverted surplus when both sides are hypermobile vs nominal goal.
 * Aggregate {@link computeLateralization} may still use ROM delta.
 * For keys outside the spectrum (e.g. foot/grounding), returns `'none'` — callers keep special handling.
 */
export function getSimpleIntakeLateralizationState(
  testKey: string,
  l: number | null,
  r: number | null,
  version: RuleDocVersion
): IntakeLateralizationState {
  const spec = computeIntakeSpectrumHighlight(testKey, l, r, version);
  if (spec === null) return 'none';
  return spectrumHighlightToIntakeState(spec);
}

/** SoP Table simple-column label for section detail / parity with intake cards. */
export function spectrumActiveColumnsToSimpleLabel(activeColumns: number[]): 'Left' | 'Right' | 'Mixed' | 'N/A' {
  if (activeColumns.length === 0) return 'N/A';
  const { left, right } = getSpectrumSimpleSideHighlight(activeColumns);
  if (left && right) return 'Mixed';
  if (left) return 'Left';
  if (right) return 'Right';
  return 'N/A';
}

export const SPECTRUM_DEFINITIONS: SpectrumDefinition[] = [
  {
    key: 'CervicalAxialRotation',
    label: 'Cervical Axial Rotation',
    goal: '30°',
    nominal: 30,
    cells: ['No Limit', 'Only [R]', '[R] > [L]', '[L] > [R]', 'Only [L]', 'No Limit'],
  },
  {
    key: 'CervicalLateralFlexion',
    label: 'Cervical Lateral Flexion',
    goal: '30°',
    nominal: 30,
    cells: ['No Limit', 'Only [L]', '[L] > [R]', '[R] > [L]', 'Only [R]', 'No Limit'],
  },
  {
    key: 'ShoulderIR',
    label: 'Shoulder IR',
    goal: '70°',
    nominal: 70,
    cells: ['No Limit', 'Only [L]', '[L] > [R]', '[R] > [L]', 'Only [R]', 'No Limit'],
  },
  {
    key: 'ShoulderER',
    label: 'Shoulder ER',
    goal: '90°',
    nominal: 90,
    cells: ['No Limit', 'Only [R]', '[R] > [L]', '[L] > [R]', 'Only [L]', 'No Limit'],
  },
  {
    key: 'ShoulderFlexion',
    label: 'Shoulder Flexion',
    goal: '120°',
    nominal: 120,
    cells: ['No Limit', 'Only [L]', '[L] > [R]', '[R] > [L]', 'Only [R]', 'No Limit'],
  },
  {
    key: 'ShoulderAbduction',
    label: 'Shoulder Abduction',
    goal: '60°',
    nominal: 60,
    cells: ['No Limit', 'Only [R]', '[R] > [L]', '[L] > [R]', 'Only [L]', 'No Limit'],
  },
  {
    key: 'PelvicRotation',
    label: 'Pelvic Rotation',
    goal: '70°',
    nominal: 70,
    cells: ['No Limit', 'Only [R]', '[R] > [L]', '[L] > [R]', 'Only [L]', 'No Limit'],
  },
  {
    key: 'HipExtension',
    label: 'Hip Extension',
    goal: '0°',
    nominal: 0,
    passThresholdOverride: 0,
    cells: ['No Limit', 'Only [R]', '[R] > [L]', '[L] > [R]', 'Only [L]', 'No Limit'],
  },
  {
    key: 'StraightLegRaise',
    label: 'Straight Leg Raise',
    goal: '90°',
    nominal: 90,
    cells: ['No Limit', 'Only [R]', '[R] ≥ [L]*', '[L] ≥ [R]*', 'Only [L]', 'No Limit'],
  },
  {
    key: 'HipAbduction',
    label: 'Hip Abduction',
    goal: '45°',
    nominal: 45,
    cells: ['No Limit', 'Only [L]', '[L] > [R]', '[R] > [L]', 'Only [R]', 'No Limit'],
  },
  {
    key: 'FemoralIR',
    label: 'Femoral IR',
    goal: '40°',
    nominal: 40,
    cells: ['No Limit', 'Only [R]', '[R] > [L]', '[L] > [R]', 'Only [L]', 'No Limit'],
  },
  {
    key: 'FemoralER',
    label: 'Femoral ER',
    goal: '45°',
    nominal: 45,
    cells: ['No Limit', 'Only [L]', '[L] > [R]', '[R] > [L]', 'Only [R]', 'No Limit'],
  },
  {
    key: 'FootPresentation',
    label: 'Foot Presentation',
    goal: 'Neutral',
    nominal: null,
    cells: ['Balanced', '[L] Supinated', 'Both Supinated [L] > [R]', 'Both Supinated [R] > [L]', '[R] Supinated', 'Balanced'],
  },
  {
    key: 'GroundingPerformance',
    label: 'Grounding Performance',
    goal: 'No Bias',
    nominal: null,
    cells: ['Balanced', '[L] Biased Power > [R]', '[L] Biased Power > [R]', '[R] Biased Power > [L]', '[R] Biased Power > [L]', 'Balanced'],
  },
];

export const SPECTRUM_DEFINITIONS_LEGACY: SpectrumDefinition[] = SPECTRUM_DEFINITIONS.map((def) =>
  def.key === 'HipExtension'
    ? {
        key: 'HipAdductionObers',
        label: "Hip Adduction (Ober's)",
        goal: 'Knee to Table',
        nominal: null,
        cells: ['No Limit', 'Only [R]', '[R] > [L]', '[L] > [R]', 'Only [L]', 'No Limit'],
      }
    : def
);

export const getSpectrumDefinitions = (version: RuleDocVersion): SpectrumDefinition[] =>
  version === 'legacy' ? SPECTRUM_DEFINITIONS_LEGACY : SPECTRUM_DEFINITIONS;

const getBaseNoDataRow = (testKey: string): SpectrumComputedRow => ({
  testKey,
  rowState: 'No Data',
  activeColumns: [],
  passThreshold: null,
  leftPass: null,
  rightPass: null,
  leftDisplay: null,
  rightDisplay: null,
});

const formatDisplayValue = (value: number | null, suffix = '°'): string | null =>
  value === null ? null : `${value}${suffix}`;

const parseFootPresentationState = (
  footPresentation: string | null
): {
  rowState: SpectrumComputedRow['rowState'];
  activeColumns: number[];
  leftPass: boolean | null;
  rightPass: boolean | null;
  leftDisplay: string | null;
  rightDisplay: string | null;
} => {
  if (!footPresentation) {
    return {
      rowState: 'No Data',
      activeColumns: [],
      leftPass: null,
      rightPass: null,
      leftDisplay: null,
      rightDisplay: null,
    };
  }

  const normalized = footPresentation.toLowerCase();
  const contains = (token: string): boolean => normalized.includes(token);

  if (contains('neutral') || contains('balanced')) {
    return {
      rowState: 'Functional Occupancy',
      activeColumns: [0, 5],
      leftPass: true,
      rightPass: true,
      leftDisplay: 'Neutral',
      rightDisplay: 'Neutral',
    };
  }

  if (contains('r supinated l pronated') || contains('[r] supinated')) {
    return {
      rowState: 'Biased Lateralization',
      activeColumns: [4],
      leftPass: true,
      rightPass: false,
      leftDisplay: 'Pronated',
      rightDisplay: 'Supinated',
    };
  }

  if (contains('r pronated l supinated') || contains('[l] supinated')) {
    return {
      rowState: 'Biased Lateralization',
      activeColumns: [1],
      leftPass: false,
      rightPass: true,
      leftDisplay: 'Supinated',
      rightDisplay: 'Pronated',
    };
  }

  if (contains('l>r supinated') || contains('[l] > [r]')) {
    return {
      rowState: 'Mixed Lateralization',
      activeColumns: [2],
      leftPass: false,
      rightPass: false,
      leftDisplay: 'Supinated',
      rightDisplay: 'Supinated',
    };
  }

  if (contains('r>l supinated') || contains('[r] > [l]')) {
    return {
      rowState: 'Mixed Lateralization',
      activeColumns: [3],
      leftPass: false,
      rightPass: false,
      leftDisplay: 'Supinated',
      rightDisplay: 'Supinated',
    };
  }

  // Pronation-dominance values still indicate bilateral non-neutral stance.
  if (contains('l>r pronated')) {
    return {
      rowState: 'Mixed Lateralization',
      activeColumns: [2],
      leftPass: false,
      rightPass: false,
      leftDisplay: 'Pronated',
      rightDisplay: 'Pronated',
    };
  }

  if (contains('r>l pronated')) {
    return {
      rowState: 'Mixed Lateralization',
      activeColumns: [3],
      leftPass: false,
      rightPass: false,
      leftDisplay: 'Pronated',
      rightDisplay: 'Pronated',
    };
  }

  return {
    rowState: 'Insufficient Data',
    activeColumns: [],
    leftPass: null,
    rightPass: null,
    leftDisplay: footPresentation,
    rightDisplay: footPresentation,
  };
};

const computeGroundingBias = (
  parsed: ParsedValues
): {
  rowState: SpectrumComputedRow['rowState'];
  activeColumns: number[];
  leftPass: boolean | null;
  rightPass: boolean | null;
  leftDisplay: string | null;
  rightDisplay: string | null;
} => {
  const { leftActual, rightActual, leftPerceived, rightPerceived } = parsed.groundingDetailed;
  const activeMode = parsed.groundingMode;
  const fallbackMode = activeMode === 'dynamometer' ? 'perceived' : 'dynamometer';

  const activeLeft = activeMode === 'dynamometer' ? leftActual : leftPerceived;
  const activeRight = activeMode === 'dynamometer' ? rightActual : rightPerceived;
  const fallbackLeft = fallbackMode === 'dynamometer' ? leftActual : leftPerceived;
  const fallbackRight = fallbackMode === 'dynamometer' ? rightActual : rightPerceived;

  const modeUsed =
    activeLeft !== null && activeRight !== null
      ? activeMode
      : fallbackLeft !== null && fallbackRight !== null
        ? fallbackMode
        : activeMode;

  const leftValue = modeUsed === 'dynamometer' ? leftActual : leftPerceived;
  const rightValue = modeUsed === 'dynamometer' ? rightActual : rightPerceived;
  const leftPass = leftValue !== null ? true : null;
  const rightPass = rightValue !== null ? true : null;

  const modePrefix = modeUsed === 'dynamometer' ? 'D' : 'P';
  const leftDisplay = leftValue !== null ? `${modePrefix}:${leftValue}` : null;
  const rightDisplay = rightValue !== null ? `${modePrefix}:${rightValue}` : null;

  if (leftPass === null && rightPass === null) {
    return { rowState: 'No Data', activeColumns: [], leftPass, rightPass, leftDisplay, rightDisplay };
  }
  if (leftPass === null || rightPass === null) {
    return { rowState: 'Insufficient Data', activeColumns: [], leftPass, rightPass, leftDisplay, rightDisplay };
  }

  if (leftValue === rightValue) {
    return {
      rowState: 'Functional Occupancy',
      activeColumns: [0, 5],
      leftPass,
      rightPass,
      leftDisplay,
      rightDisplay,
    };
  }

  if (leftValue !== null && rightValue !== null && leftValue < rightValue) {
    return {
      rowState: 'Biased Lateralization',
      activeColumns: [4],
      leftPass,
      rightPass,
      leftDisplay,
      rightDisplay,
    };
  }

  if (leftValue !== null && rightValue !== null && leftValue > rightValue) {
    return {
      rowState: 'Biased Lateralization',
      activeColumns: [1],
      leftPass,
      rightPass,
      leftDisplay,
      rightDisplay,
    };
  }

  return {
    rowState: 'Mixed Lateralization',
    activeColumns: [2, 3],
    leftPass,
    rightPass,
    leftDisplay,
    rightDisplay,
  };
};

export function computeSpectrumRows(
  parsed: ParsedValues,
  lateralization: LateralizationResult,
  orientation: OrientationResult,
  version: RuleDocVersion = 'current'
): SpectrumResult {
  const rowsByKey: Record<string, SpectrumComputedRow> = {};
  const spectrumDefinitions = getSpectrumDefinitions(version);

  spectrumDefinitions.forEach((def) => {
    if (def.key === 'FootPresentation') {
      const foot = parseFootPresentationState(parsed.footPresentation);
      rowsByKey[def.key] = {
        testKey: def.key,
        rowState: foot.rowState,
        activeColumns: foot.activeColumns,
        passThreshold: null,
        leftPass: foot.leftPass,
        rightPass: foot.rightPass,
        leftDisplay: foot.leftDisplay,
        rightDisplay: foot.rightDisplay,
      };
      return;
    }

    if (def.key === 'GroundingPerformance') {
      const grounding = computeGroundingBias(parsed);
      rowsByKey[def.key] = {
        testKey: def.key,
        rowState: grounding.rowState,
        activeColumns: grounding.activeColumns,
        passThreshold: null,
        leftPass: grounding.leftPass,
        rightPass: grounding.rightPass,
        leftDisplay: grounding.leftDisplay,
        rightDisplay: grounding.rightDisplay,
      };
      return;
    }

    const test = lateralization.tests.find((entry) => entry.testKey === def.key);
    if (!test) {
      rowsByKey[def.key] = getBaseNoDataRow(def.key);
      return;
    }

    const l = test.leftVal;
    const r = test.rightVal;

    if (def.key === 'HipAdductionObers') {
      const partial = computeHipAdductionObersSpectrumPartial(def, l, r);
      rowsByKey[def.key] = {
        testKey: def.key,
        rowState: partial.rowState,
        activeColumns: partial.activeColumns,
        passThreshold: partial.passThreshold,
        leftPass: partial.leftPass,
        rightPass: partial.rightPass,
        leftDisplay: formatDisplayValue(l, ''),
        rightDisplay: formatDisplayValue(r, ''),
      };
      return;
    }

    const partial = computeStandardBilateralSpectrumPartial(def, l, r);
    rowsByKey[def.key] = {
      testKey: def.key,
      rowState: partial.rowState,
      activeColumns: partial.activeColumns,
      passThreshold: partial.passThreshold,
      leftPass: partial.leftPass,
      rightPass: partial.rightPass,
      leftDisplay: formatDisplayValue(l),
      rightDisplay: formatDisplayValue(r),
    };
  });

  const spectrumColumn: SpectrumResult['spectrumColumn'] =
    lateralization.pattern === 'Balanced' || lateralization.pattern === 'Mixed'
      ? 3
      : orientation.pattern === 'Neutral'
        ? 1
        : 2;

  return { spectrumColumn, definitions: spectrumDefinitions, rowsByKey };
}
